'use client';

import { TReviewData } from '@/lib/types/review';
// import CarCoverSelector from './CarCoverSelector';
import { createStore } from 'zustand';
import { createContext, useRef, useEffect } from 'react';
import { notFound, useParams, useRouter, useSearchParams } from 'next/navigation';
import { compareRawStrings, getLatestYear, getSelectedProductIndex, filterProductsBySearchParams } from '@/lib/utils';
import { IProductData, TPathParams, TQueryParams, modelDataShopifyTransformer } from '@/utils';
import { TProductReviewSummary } from '@/lib/db/review';
import { bodyTrimCustomSortOrder, displayColorCustomSortOrder } from '@/lib/constants';
import { sortByYearGenerationAndSubmodel, sortByDisplayColor } from '@/lib/utils/sorting';
import { getSequentialAutoSelections } from '@/lib/utils/auto-selection';
type CarSelectionStore = ReturnType<typeof createCarSelectionStore>;
export type TQuery = {
  type: string;
  coverType: string;
  year: string;
  make: string;
  model: string;
  submodel: string;
  secondSubmodel: string;
  thirdSubmodel: string;
  submodel1: string;
  submodel2: string;
  submodel3: string;
  parent_generation: string;
  body: string;
  modelDetail: string;
  cab: string;
  bed: string;
  mirror: string;
  color: string;
};
interface ICarCoverProps {
  modelData: IProductData[];
  initialModelData: IProductData[];
  selectedProduct: IProductData;
  reviewData: TReviewData[];
  reviewDataSummary: TProductReviewSummary;
  reviewImages: TReviewData[];
}
interface SelectionConfig {
  mode: 'single' | 'multiple';
  maxSelections?: number; // for multiple
  minSelections?: number; // for multiple
}
export interface ICarCoverSelectionState extends ICarCoverProps {
  setQuery: (newQuery: Partial<TQuery>) => void;
  setModelData: () => void;
  hasSubmodels: () => boolean;
  setSelectedProduct: (newProduct: IProductData) => void;
  setFeaturedImage: (newImage: string) => void;
  setSelectedColor: (color: string) => void;
  featuredImage: string;
  selectedColor: string;
  query: TQuery;
  paramsYear: string;
  setReviewData: (newReviewData: TReviewData[]) => void;
  addReviewData: (data: TReviewData[]) => void;
  setReviewDataSummary: (newReviewDataSummary: TProductReviewSummary) => void;
  reviewImageTracker: Record<string, boolean>;
  setReviewImageTracker: (newImageTracker: Record<string, boolean>) => void;
  customerSelectedYear: string;
  setCustomerSelectedYear: (year: string) => void;
  selectedYearGeneration: string;
  setSelectedYearGeneration: (year: string) => void;
  selectedBodyTrim: string;
  setSelectedBodyTrim: (bodyTrim: string) => void;
  selectedBedSize?: string;
  setSelectedBedSize?: (bedSize: string) => void;
  selectedMirror?: string;
  setSelectedMirror?: (mirror: string) => void;
  selectedYear: string;
  setSelectedYear: (year: string) => void;
  selectedModelDetail: string;
  setSelectedModelDetail: (modelDetail: string) => void;
  isComplete: boolean;
  selectionConfig: SelectionConfig;
  setSelectionConfig: (config: SelectionConfig) => void;
}
const createCarSelectionStore = ({
  initialModelData,
  params,
  queryParams,
  initialReviewData,
  initialReviewDataSummary,
  initialReviewImages,
  isComplete = false
}: {
  initialModelData: IProductData[];
  params: TPathParams;
  queryParams: TQueryParams;
  initialReviewData: TReviewData[];
  initialReviewDataSummary: TProductReviewSummary;
  initialReviewImages: TReviewData[];
  isComplete: boolean;
}) => {
  const hasNoSubmodels = initialModelData.every(model => !model.submodel1 && !model.submodel2 && !model.submodel3);
  if (initialModelData.length === 0) {
    notFound();
  }
  const sortedInitialDataByYearGenerationAndSubmodel: IProductData[] = sortByYearGenerationAndSubmodel(sortByDisplayColor(initialModelData, displayColorCustomSortOrder), bodyTrimCustomSortOrder);

  // Until we have actual photos, we don't want to have repeat photos in reviews
  const reviewImageTracker: Record<string, boolean> = {};
  initialReviewData.forEach(reviewData => {
    !!reviewData.review_image && reviewData.review_image.split(',').map(imageUrl => {
      if (!reviewImageTracker[imageUrl]) {
        reviewImageTracker[imageUrl] = true;
      }
    });
  });

  /**
   * Figure out if customer provided year is valid, otherwise try to get the next valid one.
   */
  const customerSelectedYear = queryParams?.year;
  const selectedProductIndex = getSelectedProductIndex(sortedInitialDataByYearGenerationAndSubmodel, queryParams);
  const productEndYear = getLatestYear(sortedInitialDataByYearGenerationAndSubmodel[selectedProductIndex]);
  let finalYear: number | null = Number(customerSelectedYear);

  // Handle invalid year values - fallback to null (blank)
  if (isNaN(finalYear) || finalYear <= 0) {
    finalYear = null;
  } else if (productEndYear && finalYear > productEndYear) {
    finalYear = productEndYear;
  }

  // Auto-select submodels if only one option is available
  const selectedColor = sortedInitialDataByYearGenerationAndSubmodel[selectedProductIndex]?.display_color || 'Black Red Stripe';
  const selectedYearValue = finalYear?.toString() ?? '';
  const autoSelections = getSequentialAutoSelections(sortedInitialDataByYearGenerationAndSubmodel, ['submodel1', 'submodel2', 'submodel3'], {
    display_color: selectedColor,
    year: selectedYearValue
  });
  return createStore<ICarCoverSelectionState>()((set, get) => ({
    modelData: sortedInitialDataByYearGenerationAndSubmodel,
    initialModelData: sortedInitialDataByYearGenerationAndSubmodel,
    query: {
      year: queryParams?.year && customerSelectedYear || '',
      type: params?.productType ?? '',
      coverType: params?.coverType ?? 'premium-plus',
      make: params?.make ?? '',
      model: params?.model ?? '',
      submodel: queryParams?.submodel ?? '',
      secondSubmodel: queryParams?.submodel2 ?? '',
      thirdSubmodel: queryParams?.submodel3 ?? '',
      submodel1: queryParams?.submodel ?? '',
      submodel2: queryParams?.submodel2 ?? '',
      submodel3: queryParams?.submodel3 ?? '',
      parent_generation: params?.year && hasNoSubmodels || queryParams?.submodel ? params?.year as string : ''
    },
    selectedProduct: sortedInitialDataByYearGenerationAndSubmodel[selectedProductIndex],
    featuredImage: sortedInitialDataByYearGenerationAndSubmodel[selectedProductIndex]?.mainImage,
    selectedColor: sortedInitialDataByYearGenerationAndSubmodel[selectedProductIndex]?.display_color || 'Black Red Stripe',
    isComplete,
    reviewImages: initialReviewImages,
    reviewImageTracker,
    setReviewImageTracker: (newImageTracker: Record<string, boolean>) => {
      set(() => ({
        reviewImageTracker: newImageTracker
      }));
    },
    setSelectedProduct: (newProduct: IProductData) => {
      set(() => ({
        selectedProduct: newProduct,
        featuredImage: newProduct.product?.split(',')[0] ?? ''
      }));
    },
    setSelectedColor: (newColor: string) => set(() => ({
      selectedColor: newColor
    })),
    setFeaturedImage: (newImage: string) => set(() => ({
      featuredImage: newImage
    })),
    hasSubmodels: () => {
      const {
        modelData
      } = get();
      return modelData.some(model => !!model.submodel1 || !!model.submodel2 || !!model.submodel3);
    },
    setQuery: (newQuery: Partial<TQuery>) => {
      set(state => ({
        ...state,
        query: {
          ...state.query,
          ...newQuery
        }
      }));
      const {
        setModelData
      } = get();
      setModelData();
      const newModelData = get().modelData;
      if (newModelData.length > 0 && Object.values(newQuery).some(v => !!v)) {
        set({
          selectedProduct: newModelData[0]
        });
      }
    },
    setModelData: () => {
      const {
        initialModelData,
        query: newQuery
      } = get();
      let filteredData = initialModelData;
      if (newQuery.type) {
        filteredData = filteredData.filter(sku => {
          return compareRawStrings(sku.type, newQuery.type);
        });
      }
      if (newQuery.make) {
        filteredData = filteredData.filter(sku => compareRawStrings(sku.make, newQuery.make as string));
      }
      if (newQuery.model) {
        filteredData = filteredData.filter(sku => compareRawStrings(sku.model, newQuery.model as string));
      }
      if (newQuery.year) {
        filteredData = filteredData.filter(sku => sku.year_options?.includes(newQuery.year as string));
      }
      if (newQuery.submodel) {
        filteredData = filteredData.filter(sku => compareRawStrings(sku.submodel1, newQuery.submodel as string));
      }
      if (newQuery.secondSubmodel) {
        filteredData = filteredData.filter(sku => compareRawStrings(sku.submodel2, newQuery.secondSubmodel as string));
      }
      if (newQuery.thirdSubmodel) {
        filteredData = filteredData.filter(sku => compareRawStrings(sku.submodel3, newQuery.thirdSubmodel as string));
      }
      set({
        modelData: filteredData
      });
    },
    reviewData: initialReviewData,
    setReviewData: (newReviewData: TReviewData[]) => {
      set(() => ({
        reviewData: newReviewData
      }));
    },
    addReviewData: (data: TReviewData[]) => set(state => ({
      reviewData: [...state.reviewData, ...data]
    })),
    reviewDataSummary: initialReviewDataSummary,
    setReviewDataSummary: (newReviewDataSummary: TProductReviewSummary) => {
      set(() => ({
        reviewDataSummary: newReviewDataSummary
      }));
    },
    setReviewsWithImages: (newReviewImages: TReviewData[]) => {
      set(() => ({
        reviewImages: newReviewImages
      }));
    },
    paramsYear: params.year || '',
    customerSelectedYear: customerSelectedYear || '',
    setCustomerSelectedYear: (year: string) => {
      localStorage.setItem('heroDropdownYear', year);
      set(() => ({
        customerSelectedYear: year
      }));
    },
    selectedYearGeneration: isComplete ? sortedInitialDataByYearGenerationAndSubmodel[selectedProductIndex]?.year_generation ?? '' : '',
    setSelectedYearGeneration: (year: string) => set(() => ({
      selectedYearGeneration: year
    })),
    selectedBodyTrim: isComplete ? sortedInitialDataByYearGenerationAndSubmodel[selectedProductIndex]?.submodel1 ?? '' : autoSelections.submodel1 || '',
    setSelectedBodyTrim: (bodyTrim: string) => set(() => ({
      selectedBodyTrim: bodyTrim
    })),
    selectedBedSize: isComplete ? sortedInitialDataByYearGenerationAndSubmodel[selectedProductIndex]?.submodel2 ?? '' : autoSelections.submodel2 || '',
    setSelectedBedSize: (bedSize: string) => set(() => ({
      selectedBedSize: bedSize
    })),
    selectedMirror: isComplete ? sortedInitialDataByYearGenerationAndSubmodel[selectedProductIndex]?.submodel3 ?? '' : autoSelections.submodel3 || '',
    setSelectedMirror: (mirror: string) => set(() => ({
      selectedMirror: mirror
    })),
    selectedYear: finalYear?.toString() ?? '',
    setSelectedYear: (year: string) => set(() => ({
      selectedYear: year
    })),
    selectedModelDetail: isComplete ? sortedInitialDataByYearGenerationAndSubmodel[selectedProductIndex]?.model_detail ?? '' : '',
    setSelectedModelDetail: (modelDetail: string) => set(() => ({
      selectedModelDetail: modelDetail
    })),
    selectionConfig: {
      mode: 'single',
      minSelections: 1,
      maxSelections: 1
    },
    setSelectionConfig: config => set({
      selectionConfig: config
    })
  }));
};
export const CarSelectionContext = createContext<CarSelectionStore | null>(null);
const CarSelectionProvider = ({
  children,
  initialState
}: {
  children: React.ReactNode;
  initialState: any;
}) => {
  const {
    modelData: modelDataProps,
    reviewData,
    reviewDataSummary,
    reviewImages
  } = initialState;
  const router = useRouter();
  const pathParams = useParams<TPathParams>();
  const searchParams = useSearchParams();

  // Convert URLSearchParams to object
  const searchParamsObj: Record<string, string> = {};
  searchParams.forEach((value, key) => {
    searchParamsObj[key] = value;
  });
  const submodelParams = searchParamsObj?.submodel ?? '';
  const secondSubmodelParams = searchParamsObj?.submodel2 ?? '';
  const thirdSubmodelParams = searchParamsObj?.submodel3 ?? '';
  if (modelDataProps.length === 0) {
    router.push('/404');
  }

  // Have to write it out with null check in case query param empty. Can't just destructure.
  const queryParams = {
    submodel: submodelParams,
    submodel1: submodelParams,
    secondSubmodel: secondSubmodelParams,
    thirdSubmodel: thirdSubmodelParams,
    submodel2: secondSubmodelParams,
    submodel3: thirdSubmodelParams,
    body: searchParamsObj?.body ?? '',
    modelDetail: searchParamsObj?.submodel ?? '',
    cab: searchParamsObj?.cab ?? '',
    bed: searchParamsObj?.bed ?? '',
    mirror: searchParamsObj?.mirror ?? '',
    color: searchParamsObj?.color ?? '',
    year: searchParamsObj?.year ?? ''
  };
  const modelData = modelDataShopifyTransformer({
    data: modelDataProps,
    params: pathParams ?? {} as TPathParams,
    queryParams
  });

  // Client-side validation and filtering
  const filteredData = filterProductsBySearchParams(modelData, queryParams);

  // Check if search params result in valid selection
  const hasSearchParams = Object.values(queryParams).some(param => param && param.trim() !== '');
  const onlyYearParam = queryParams.year && Object.entries(queryParams).filter(([key, value]) => key !== 'year' && value && value.trim() !== '').length === 0;
  const isValidSelection = !hasSearchParams || onlyYearParam || filteredData.length === 1;
  const shouldCleanUrl = hasSearchParams && !onlyYearParam && (filteredData.length === 0 || filteredData.length > 1);

  // Clean URL if invalid params
  useEffect(() => {
    if (shouldCleanUrl) {
      const basePath = `/${pathParams?.productType}/${pathParams?.coverType}/${pathParams?.make}/${pathParams?.model || ''}`;
      // console.log('@@@ router.replace (CarSelectionContext)', { basePath, stack: new Error().stack });
      router.replace(basePath, {
        scroll: false
      });
    }
  }, [shouldCleanUrl, router, pathParams]);
  const finalIsComplete = filteredData.length === 1;
  const store = useRef(createCarSelectionStore({
    params: pathParams ?? {} as TPathParams,
    queryParams,
    initialModelData: modelData,
    initialReviewData: reviewData as TReviewData[],
    initialReviewDataSummary: reviewDataSummary,
    initialReviewImages: reviewImages,
    isComplete: finalIsComplete
  })).current;
  return <CarSelectionContext.Provider value={store} data-sentry-element="unknown" data-sentry-component="CarSelectionProvider" data-sentry-source-file="CarSelectionContext.tsx">{children}</CarSelectionContext.Provider>;
};
export default CarSelectionProvider;